import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Download, AlertCircle, Loader2 } from "lucide-react";

const fmt = (s) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

/* مشغل صوتي موحد لرسائل الدعم (واتساب + التطبيق): تشغيل/إيقاف، شريط تقدم
   قابل للسحب، المدة، إعادة، تحميل — مع حالة خطأ وإعادة محاولة. */
export const AudioPlayer = ({ src, durationHint = 0, accent = "#075E54", testId = "audio-player", fileName = "voice-message" }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(durationHint || 0);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setErr(false); setPlaying(false); setCur(0); setDur(durationHint || 0); }, [src, durationHint]);

  const syncDur = (a) => {
    const d = a.duration;
    if (isFinite(d) && d > 0) setDur(d);
    else if (d === Infinity) {
      // تسجيلات webm بلا مدة بالميتاداتا — خدعة القفز لآخر الملف لقراءتها
      const onSeek = () => { if (isFinite(a.duration) && a.duration > 0) setDur(a.duration); a.currentTime = 0; a.removeEventListener("seeked", onSeek); };
      a.addEventListener("seeked", onSeek);
      try { a.currentTime = 1e7; } catch (e) { a.removeEventListener("seeked", onSeek); }
    }
  };

  const toggle = async () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); return; }
    try { setLoading(true); await a.play(); } catch (e) { setErr(true); } finally { setLoading(false); }
  };

  const seek = (e) => {
    const a = ref.current;
    if (!a || !dur || !isFinite(dur)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * dur;
    setCur(a.currentTime);
  };

  const replay = () => {
    const a = ref.current;
    if (!a) return;
    a.currentTime = 0;
    a.play().catch(() => setErr(true));
  };

  const retry = () => { setErr(false); const a = ref.current; if (a) { a.load(); } };

  if (err) {
    return (
      <div className="flex items-center gap-2 text-[11px] rounded-xl px-2.5 py-2 mb-1"
           style={{ background: "#fee2e2", color: "#b91c1c" }} data-testid={`${testId}-error`}>
        <AlertCircle size={13} className="shrink-0" /> تعذر تشغيل التسجيل
        <button onClick={retry} className="font-bold underline" data-testid={`${testId}-retry`}>إعادة المحاولة</button>
        <a href={src} download={fileName} className="font-bold underline" data-testid={`${testId}-dl-fallback`}>تحميل</a>
      </div>
    );
  }

  const pct = dur && isFinite(dur) ? Math.min(100, (cur / dur) * 100) : 0;
  return (
    <div className="flex items-center gap-2 rounded-2xl px-2 py-1.5 mb-1 min-w-[210px] max-w-[270px]"
         style={{ background: "rgba(0,0,0,0.05)" }} data-testid={testId} dir="ltr">
      <audio ref={ref} src={src} preload="metadata"
             onLoadedMetadata={(e) => syncDur(e.currentTarget)}
             onDurationChange={(e) => { const d = e.currentTarget.duration; if (isFinite(d) && d > 0) setDur(d); }}
             onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
             onPlay={() => setPlaying(true)}
             onPause={() => setPlaying(false)}
             onEnded={() => { setPlaying(false); setCur(0); }}
             onError={() => setErr(true)} />
      <button onClick={toggle}
              className="w-8 h-8 rounded-full text-white flex items-center justify-center shrink-0"
              style={{ background: accent }} data-testid={`${testId}-toggle`}
              aria-label={playing ? "إيقاف" : "تشغيل"}>
        {loading ? <Loader2 size={13} className="animate-spin" />
          : playing ? <Pause size={13} /> : <Play size={13} style={{ marginLeft: 2 }} />}
      </button>
      <div className="flex-1 cursor-pointer py-1.5" onClick={seek} data-testid={`${testId}-bar`}>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.12)" }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent, transition: "width 150ms linear" }} />
        </div>
      </div>
      <span className="text-[10px] font-bold opacity-60 tabular-nums shrink-0" data-testid={`${testId}-time`}>
        {fmt(cur)}&nbsp;/&nbsp;{fmt(dur)}
      </span>
      <button onClick={replay} title="إعادة التشغيل" className="opacity-50 hover:opacity-100 shrink-0" data-testid={`${testId}-replay`}>
        <RotateCcw size={12} />
      </button>
      <a href={src} download={fileName} title="تحميل التسجيل" className="opacity-50 hover:opacity-100 shrink-0" data-testid={`${testId}-download`}>
        <Download size={12} />
      </a>
    </div>
  );
};

export default AudioPlayer;
